import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useFormik } from "formik";
import { debounce } from "lodash";

import { Grid, Button, Container, CircularProgress } from "@material-ui/core";

import AutoCompleteField from "components/_refactor/fields/autocomplete";

import themeColorsDesingSystem from "assets/theme/ds-colors";

import {
  loadLinhaVendaOptions,
  loadGradeOptions,
  loadCollectionOptions,
} from "../../../shared/utils/grade-filter-options";

export default function Filters({ handleSearch, searching, idMarca }) {
  const [linhasVenda, setLinhasVenda] = useState([]);
  const [grades, setGrades] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState({
    linhasVenda: false,
    grades: false,
    collections: false,
  });
  const linhaVendaLoadSeqRef = useRef(0);
  const gradeLoadSeqRef = useRef(0);
  const collectionLoadSeqRef = useRef(0);
  const idMarcaRef = useRef(idMarca);
  idMarcaRef.current = idMarca;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      linhaVenda: null,
      grade: null,
      collection: null,
      sku: [],
    },
    onSubmit: (values) => {
      handleSearch({ ...values, initializePreSelection: true });
    },
  });

  // Leitura sempre atualizada dos valores selecionados dentro dos handlers memoizados abaixo (evita
  // fechar sobre um `formik.values` desatualizado quando o handler não foi recriado nesse render).
  const formikValuesRef = useRef(formik.values);
  formikValuesRef.current = formik.values;

  const handleChange = (key, value) => {
    formik.setFieldValue(key, value);
    if (key === "linhaVenda") {
      formik.setFieldValue("grade", null);
    }
  };

  const selectedLinhaVenda = formik.values.linhaVenda?.label ?? "";
  const selectedGrade = formik.values.grade?.label ?? "";
  const selectedCollection = formik.values.collection?.label ?? "";

  /** Se o valor selecionado de `field` não estiver mais entre as opções recarregadas (porque outro
   * filtro cascateado mudou), limpa a seleção — evita ficar com um valor "fantasma" selecionado que
   * não existe mais na lista atual. */
  const clearIfStale = useCallback((field, options) => {
    const current = formikValuesRef.current?.[field];
    if (current && !options.some((o) => o.label === current.label)) {
      formik.setFieldValue(field, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetLinhasVenda = useCallback(
    async (searchTerm = "") => {
      if (!idMarcaRef.current) {
        setLinhasVenda([]);
        return;
      }

      const seq = ++linhaVendaLoadSeqRef.current;
      setLoadingFilters((prev) => ({ ...prev, linhasVenda: true }));

      try {
        const options = await loadLinhaVendaOptions({
          idMarca: idMarcaRef.current,
          grade: selectedGrade,
          colecao: selectedCollection,
          filter: searchTerm,
        });

        if (seq !== linhaVendaLoadSeqRef.current) return;
        setLinhasVenda(options);
        if (!searchTerm) clearIfStale("linhaVenda", options);
      } catch (error) {
        if (seq !== linhaVendaLoadSeqRef.current) return;
        console.error("Erro ao buscar linhas de venda", error);
        setLinhasVenda([]);
      } finally {
        if (seq === linhaVendaLoadSeqRef.current) {
          setLoadingFilters((prev) => ({ ...prev, linhasVenda: false }));
        }
      }
    },
    [selectedGrade, selectedCollection, clearIfStale],
  );

  const handleGetGrades = useCallback(
    async (searchTerm = "") => {
      if (!idMarcaRef.current) {
        setGrades([]);
        return;
      }

      const seq = ++gradeLoadSeqRef.current;
      setLoadingFilters((prev) => ({ ...prev, grades: true }));
      try {
        const options = await loadGradeOptions({
          idMarca: idMarcaRef.current,
          linhaVenda: selectedLinhaVenda,
          colecao: selectedCollection,
          filter: searchTerm,
        });

        if (seq !== gradeLoadSeqRef.current) return;
        setGrades(options);
        if (!searchTerm) clearIfStale("grade", options);
      } catch (error) {
        if (seq !== gradeLoadSeqRef.current) return;
        console.error("Erro ao buscar grades", error);
        setGrades([]);
      } finally {
        if (seq === gradeLoadSeqRef.current) {
          setLoadingFilters((prev) => ({ ...prev, grades: false }));
        }
      }
    },
    [selectedLinhaVenda, selectedCollection, clearIfStale],
  );

  const handleGetCollections = useCallback(
    async (searchTerm = "") => {
      if (!idMarcaRef.current) {
        setCollections([]);
        return;
      }

      const seq = ++collectionLoadSeqRef.current;
      setLoadingFilters((prev) => ({ ...prev, collections: true }));
      try {
        const options = await loadCollectionOptions({
          idMarca: idMarcaRef.current,
          linhaVenda: selectedLinhaVenda,
          grade: selectedGrade,
          filter: searchTerm,
        });

        if (seq !== collectionLoadSeqRef.current) return;
        setCollections(options);
        if (!searchTerm) clearIfStale("collection", options);
      } catch (error) {
        if (seq !== collectionLoadSeqRef.current) return;
        console.error("Erro ao buscar coleções", error);
        setCollections([]);
      } finally {
        if (seq === collectionLoadSeqRef.current) {
          setLoadingFilters((prev) => ({ ...prev, collections: false }));
        }
      }
    },
    [selectedLinhaVenda, selectedGrade, clearIfStale],
  );

  const debouncedGetLinhasVenda = useMemo(
    () =>
      debounce((searchTerm) => {
        handleGetLinhasVenda(searchTerm);
      }, 650),
    [handleGetLinhasVenda],
  );

  const handleLinhaVendaInputChange = useCallback(
    (_, inputValue, reason) => {
      if (reason !== "input") return;
      debouncedGetLinhasVenda(inputValue ?? "");
    },
    [debouncedGetLinhasVenda],
  );

  const debouncedGetGrades = useMemo(
    () =>
      debounce((searchTerm) => {
        handleGetGrades(searchTerm);
      }, 650),
    [handleGetGrades],
  );

  const handleGradeInputChange = useCallback(
    (_, inputValue, reason) => {
      if (reason !== "input") return;
      debouncedGetGrades(inputValue ?? "");
    },
    [debouncedGetGrades],
  );

  const debouncedGetCollections = useMemo(
    () =>
      debounce((searchTerm) => {
        handleGetCollections(searchTerm);
      }, 650),
    [handleGetCollections],
  );

  const handleCollectionInputChange = useCallback(
    (_, inputValue, reason) => {
      if (reason !== "input") return;
      debouncedGetCollections(inputValue ?? "");
    },
    [debouncedGetCollections],
  );

  useEffect(() => {
    handleGetGrades();
    handleGetCollections();
  }, [idMarca, handleGetGrades, handleGetCollections]);

  useEffect(() => {
    handleGetLinhasVenda("");
  }, [idMarca, handleGetLinhasVenda]);

  useEffect(() => {
    return () => {
      debouncedGetLinhasVenda.cancel();
      debouncedGetGrades.cancel();
      debouncedGetCollections.cancel();
    };
  }, [debouncedGetLinhasVenda, debouncedGetGrades, debouncedGetCollections]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Container maxWidth="100%" style={{ padding: 0, marginTop: "10px" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <AutoCompleteField
              options={linhasVenda}
              label="Linha"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("linhaVenda", option)}
              onInputChange={handleLinhaVendaInputChange}
              value={formik.values.linhaVenda}
              disabled={searching}
              loading={loadingFilters.linhasVenda}
              preserveInputWhenLoading
              disableClientFilter
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AutoCompleteField
              options={grades}
              label="Grade"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("grade", option)}
              onInputChange={handleGradeInputChange}
              value={formik.values.grade}
              disabled={searching}
              loading={loadingFilters.grades}
              preserveInputWhenLoading
              disableClientFilter
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <AutoCompleteField
              options={collections}
              label="Coleção"
              optionLabelKey="label"
              optionIdKey="id"
              onChange={(option) => handleChange("collection", option)}
              onInputChange={handleCollectionInputChange}
              value={formik.values.collection}
              disabled={searching}
              loading={loadingFilters.collections}
              preserveInputWhenLoading
              disableClientFilter
            />
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ marginTop: "20px" }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={8}
            style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
          >
            <Button
              style={{
                display: "flex",
                justifyContent: "center",
                width: 160,
                backgroundColor: themeColorsDesingSystem.primary.main,
              }}
              color="primary"
              variant="contained"
              type="submit"
              disabled={searching}
            >
              {searching ? (
                <CircularProgress size={20} style={{ color: "white" }} />
              ) : (
                "Consultar"
              )}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </form>
  );
}
